import { RolodexContext } from 'features/rolodex'
import { useCallback, useContext, useEffect, useId } from 'react'
import { GeckoEntry } from 'types'

type ReactID = ReturnType<typeof useId>
type EntryID = GeckoEntry['id']

export type RolodexEntryDispenserCacheBranch = Map<EntryID, GeckoEntry>
export type RolodexEntryDispenserCache = Map<
  ReactID,
  RolodexEntryDispenserCacheBranch
>

type RolodexEntryDispenserCacheAPI = {
  upsertEntry: (entry: GeckoEntry) => void
  getEntry: (
    params: Pick<GeckoEntry, 'id' | 'generation'>
  ) => GeckoEntry | undefined
}

/**
 * A hook that provides interfaces for interacting with the Rolodex Entry Dispenser Cache.
 *
 * The Rolodex Entry Dispenser Cache is designed to ensure that when data is fetched from Rolodex (or the Rolodex Cache),
 * if the `id` and `generation` of an entry hasn't changed, the memory reference of its "GeckoEntry" class object also won't change.
 * This keys in on the `id` and `generation` properties of the entries, because `id` is unique per entry, and `generation` will
 * change whenever the data for that entry is updated.
 *
 * This allows us to include our GeckoEntry-Classes in dependency arrays, especially those that result in expensive-operations
 * (e.g., generating CDFs), without worrying about unnecessary re-renders or re-computations on data that hasn't changed.
 */
export function useGetRolodexEntryDispenserCacheInterfaces(): RolodexEntryDispenserCacheAPI {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   *                                                    Instance Vars
   * ------------------------------------------------------------------------------------------------------------------
   */
  const { entryDispenserCacheContainer } = useContext(RolodexContext)
  const reactId = useId()

  /**
   * On unmount, clear out this hook's branch of the cache.
   * - Because each hook instance has its own reactId, we can safely clear out only this branch without affecting
   *   other instances.
   */
  useEffect(() => {
    return () => {
      // Suppress React warning about the fact that `current` may change, because we always want to do this on the
      // most up-to-date version of the cache
      // eslint-disable-next-line react-hooks/exhaustive-deps
      entryDispenserCacheContainer.current.delete(reactId)
    }
    // These deps are stable over the lifetime of the component, so this is just for unmounting.
  }, [entryDispenserCacheContainer, reactId])

  /**
   * ------------------------------------------------------------------------------------------------------------------
   *                                                    Helper Functions
   *                                                    (Do not export)
   * ------------------------------------------------------------------------------------------------------------------
   */

  const findExistingEntryInCache = useCallback(
    (entryId: GeckoEntry['id']) => {
      for (const branchEntries of entryDispenserCacheContainer.current.values()) {
        const existingEntry = branchEntries.get(entryId)
        if (existingEntry) return existingEntry
      }
      return undefined
    },
    [entryDispenserCacheContainer]
  )

  const upsertEntryIntoReactIdBranch = useCallback(
    (reactId: ReactID, entry: GeckoEntry) => {
      entryDispenserCacheContainer.current.set(
        reactId,
        (entryDispenserCacheContainer.current.get(reactId) ?? new Map()).set(
          entry.id,
          entry
        )
      )
    },
    [entryDispenserCacheContainer]
  )

  /**
   * Updates all existing cached entries with a matching ID to the provided entry.  This ensures that all
   * entries only use 1 reference to the data.
   */
  const updateAllExistingCachedEntriesWithMatchingId = useCallback(
    (entry: GeckoEntry) => {
      entryDispenserCacheContainer.current
        .entries()
        .forEach(([branchReactID, branchEntries]) => {
          if (branchEntries.has(entry.id)) {
            upsertEntryIntoReactIdBranch(branchReactID, entry)
          }
        })
    },
    [entryDispenserCacheContainer, upsertEntryIntoReactIdBranch]
  )

  /**
   * ------------------------------------------------------------------------------------------------------------------
   *                                                    API Functions
   *                                                  (Values Returned)
   * ------------------------------------------------------------------------------------------------------------------
   */

  /**
   * Upserts an entry into the cache, ensuring that only one (memory) reference to each unique entry ID exists.
   */
  const upsertEntryWhileKeepingStableReferences = useCallback<
    RolodexEntryDispenserCacheAPI['upsertEntry']
  >(
    (entry) => {
      const existingEntry = findExistingEntryInCache(entry.id)

      if (existingEntry && existingEntry.generation === entry.generation) {
        // Full cache hit -- use the existing reference and return
        upsertEntryIntoReactIdBranch(reactId, existingEntry)
      } else {
        updateAllExistingCachedEntriesWithMatchingId(entry) // call because it could be a generation update
        upsertEntryIntoReactIdBranch(reactId, entry)
      }
    },
    [
      findExistingEntryInCache,
      upsertEntryIntoReactIdBranch,
      reactId,
      updateAllExistingCachedEntriesWithMatchingId,
    ]
  )

  /**
   * Gets an entry from the cache if it exists and matches the provided generation
   */
  const getEntry = useCallback<RolodexEntryDispenserCacheAPI['getEntry']>(
    ({ id: entryID, generation: entryGeneration }) => {
      const existingEntry = findExistingEntryInCache(entryID)
      if (existingEntry?.generation !== entryGeneration) return undefined
      return existingEntry
    },
    [findExistingEntryInCache]
  )

  return {
    upsertEntry: upsertEntryWhileKeepingStableReferences,
    getEntry,
  }
}
